import { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import Modal from '../components/Modal.jsx';
import { apiAffaires, apiAllocations, apiPhases } from '../api/client.js';
import MobileTaskField, { NO_TASK, taskChanged, taskPayload, taskValueOf } from './MobileTaskField.jsx';
import { lastAffaireIn, rememberLastAffaire } from '../lib/lastAffaire.js';

/**
 * Sprint App native — « Mon planning » : création / modification /
 * suppression d'une allocation sur SA propre Person (contrat §7 bis, règles
 * Sprint 16.B : `selfScheduleEnabled` + hors VIEWER, vérifiés par l'appelant ;
 * le serveur reste l'autorité via `assertCanSchedule`).
 *
 * Payloads alignés sur `CreateAllocationModal` / `EditAllocationModal` du
 * Schedule desktop et validés contre les schémas Zod de l'API :
 *  - création : `allocationCreateSchema` (POST /allocations) ;
 *  - série one-shot : `allocationUpdateSchema` (PATCH /allocations/:id) ;
 *  - occurrence d'une série récurrente : `allocationInstancePatchSchema`
 *    (PATCH /allocations/:id/instances/:date?scope=…), ou PATCH série pour
 *    « toute la série » SANS dates ni RRULE (la règle reste inchangée).
 *
 * V1 mobile volontairement réduite : pas de récurrence à la création, pas de
 * confirmation de surbooking (non bloquant côté API). Tâche partagée (#76) :
 * `taskId` / `taskName` acceptés par les trois schémas, envoyés seulement
 * s'ils changent.
 */

// Ticket #11 / #48 : heures entières, 12 h au plus (cf. schéma API).
const MAX_HOURS_PER_DAY = 12;
const HOURS_OPTIONS = Array.from({ length: MAX_HOURS_PER_DAY }, (_, i) => i + 1);

const SCOPES = [
  { value: 'this_occurrence', label: 'Cette occurrence' },
  { value: 'this_and_future', label: 'Celle-ci et les suivantes' },
  { value: 'all_occurrences', label: 'Toute la série' },
];

export default function MobileAllocationModal({
  open,
  mode, // 'create' | 'edit'
  allocation, // instance issue de /schedule (mode edit)
  defaultDate, // "YYYY-MM-DD" (mode create)
  personId,
  startDelete, // ouverte depuis la corbeille du bloc : confirmation affichée
  onClose,
  onSaved,
}) {
  const isEdit = mode === 'edit' && Boolean(allocation);
  const isSeries = isEdit && Boolean(allocation.isRecurring);

  const [affaires, setAffaires] = useState([]);
  const [affairesError, setAffairesError] = useState(null);
  const [phases, setPhases] = useState([]);
  const [affaireId, setAffaireId] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hours, setHours] = useState('8');
  const [notes, setNotes] = useState('');
  const [task, setTask] = useState(NO_TASK);
  const [scope, setScope] = useState('this_occurrence');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Réinitialise le formulaire à chaque ouverture.
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setAffaireId(allocation.affaireId ?? '');
      setPhaseId(allocation.phaseId ?? '');
      setStartDate(String(allocation.startDate ?? '').slice(0, 10));
      setEndDate(String(allocation.endDate ?? '').slice(0, 10));
      const h = Math.round(Number(allocation.hoursPerDay));
      setHours(String(Number.isFinite(h) && h >= 1 ? Math.min(h, MAX_HOURS_PER_DAY) : 8));
      setNotes(allocation.notes ?? '');
      setTask(taskValueOf(allocation));
    } else {
      setAffaireId('');
      setPhaseId('');
      setStartDate(defaultDate ?? '');
      setEndDate(defaultDate ?? '');
      setHours('8');
      setNotes('');
      setTask(NO_TASK);
    }
    setScope('this_occurrence');
    setConfirmDelete(Boolean(startDelete) && isEdit);
    setSubmitting(false);
    setError(null);
  }, [open, isEdit, allocation, defaultDate, startDelete]);

  // Affaires de SA Person (Sprint 7.2 `mineOnly`), seulement si l'affaire est
  // modifiable (création, ou allocation one-shot).
  const canChangeAffaire = !isSeries;
  useEffect(() => {
    if (!open || !canChangeAffaire) return undefined;
    let cancelled = false;
    setAffairesError(null);
    apiAffaires
      .list('active', null, { mineOnly: true })
      .then((data) => {
        if (cancelled) return;
        const list = data?.items ?? [];
        setAffaires(list);
        // synotis-support#8 — une création reprend la dernière affaire saisie.
        if (!isEdit) {
          const last = lastAffaireIn(list);
          if (last) setAffaireId((prev) => prev || last);
        }
      })
      .catch((e) => {
        if (!cancelled) setAffairesError(e.message || 'Affaires indisponibles.');
      });
    return () => {
      cancelled = true;
    };
  }, [open, canChangeAffaire]);

  // Phases de l'affaire choisie.
  useEffect(() => {
    if (!open || !affaireId) {
      setPhases([]);
      return undefined;
    }
    let cancelled = false;
    apiPhases
      .list(affaireId, 'active')
      .then((data) => {
        if (!cancelled) setPhases(data?.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setPhases([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, affaireId]);

  // L'affaire d'une allocation existante peut ne plus figurer dans `mineOnly`
  // (équipe quittée) : on la garde sélectionnable pour ne pas la perdre.
  const affaireOptions = useMemo(() => {
    const list = [...affaires];
    if (isEdit && allocation.affaire && !list.some((a) => a.id === allocation.affaireId)) {
      list.unshift({ id: allocation.affaireId, name: allocation.affaire.name });
    }
    // Affaires « planification uniquement » : planifiables (c'est leur objet).
    return list;
  }, [affaires, isEdit, allocation]);

  if (!open) return null;

  const hoursNumber = Number(hours);
  const occurrenceDate = isEdit
    ? allocation.occurrenceDate ?? String(allocation.startDate).slice(0, 10)
    : null;

  const validate = () => {
    if (!isSeries && !affaireId) return 'Affaire requise.';
    if (!Number.isInteger(hoursNumber) || hoursNumber < 1 || hoursNumber > MAX_HOURS_PER_DAY) {
      return `Heures/jour : un entier entre 1 et ${MAX_HOURS_PER_DAY}.`;
    }
    if (!isSeries) {
      if (!startDate || !endDate) return 'Dates requises.';
      if (startDate > endDate) return 'La date de fin précède la date de début.';
    }
    return null;
  };

  const submit = async () => {
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return;
    }
    // Tâche : à la création si choisie ; en édition si modifiée (une tâche
    // inchangée relancerait `resolveTaskId` pour rien).
    const taskFields = isEdit
      ? taskChanged(task, allocation) ? taskPayload(task) : {}
      : task.taskId || task.taskName ? taskPayload(task) : {};
    setSubmitting(true);
    setError(null);
    try {
      if (!isEdit) {
        // Sprint 8.3 — facturabilité héritée de l'affaire (cf. desktop).
        const affaire = affaires.find((a) => a.id === affaireId);
        await apiAllocations.create({
          personId,
          affaireId,
          phaseId: phaseId || null,
          startDate,
          endDate,
          hoursPerDay: hoursNumber,
          notes: notes.trim() || null,
          ...taskFields,
          billable: affaire?.nonBillable !== true,
          status: 'CONFIRMED',
          recurrenceRule: null,
        });
        rememberLastAffaire(affaireId);
      } else if (!isSeries) {
        await apiAllocations.update(allocation.id, {
          affaireId,
          phaseId: phaseId || null,
          startDate,
          endDate,
          hoursPerDay: hoursNumber,
          notes: notes.trim() || null,
          ...taskFields,
        });
      } else if (scope === 'all_occurrences') {
        // Série entière : champs scalaires seulement (ni dates ni RRULE —
        // `.partial()` ⇒ clés absentes = inchangées côté serveur).
        await apiAllocations.update(allocation.id, {
          phaseId: phaseId || null,
          hoursPerDay: hoursNumber,
          notes: notes.trim() || null,
          ...taskFields,
        });
      } else {
        await apiAllocations.patchInstance(allocation.id, occurrenceDate, scope, {
          phaseId: phaseId || null,
          hoursPerDay: hoursNumber,
          notes: notes.trim() || null,
          ...taskFields,
        });
      }
      onSaved(isEdit ? 'Allocation modifiée.' : 'Allocation créée.');
    } catch (e) {
      setError(e.message || 'Enregistrement impossible.');
      setSubmitting(false);
    }
  };

  const remove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (!isSeries || scope === 'all_occurrences') {
        await apiAllocations.archive(allocation.id);
      } else {
        await apiAllocations.deleteInstance(allocation.id, occurrenceDate, scope);
      }
      onSaved('Allocation supprimée.');
    } catch (e) {
      setError(e.message || 'Suppression impossible.');
      setSubmitting(false);
      setConfirmDelete(false);
    }
  };

  const close = submitting ? () => {} : onClose;

  return (
    <Modal
      open={open}
      onClose={close}
      title={isEdit ? 'Modifier l’allocation' : 'Nouvelle allocation'}
      subtitle={isSeries ? 'Occurrence d’une série récurrente' : undefined}
      size="sm"
      footer={
        <div className="m-modal-footer">
          {isEdit ? (
            confirmDelete ? (
              <button
                type="button"
                className="m-btn m-btn--danger"
                onClick={remove}
                disabled={submitting}
              >
                <Trash2 size={15} /> Confirmer
              </button>
            ) : (
              <button
                type="button"
                className="m-iconbtn m-iconbtn--danger"
                onClick={() => setConfirmDelete(true)}
                disabled={submitting}
                title="Supprimer"
                aria-label="Supprimer"
              >
                <Trash2 size={17} />
              </button>
            )
          ) : null}
          <span className="m-modal-footer__spacer" />
          <button type="button" className="m-btn" onClick={close} disabled={submitting}>
            Annuler
          </button>
          <button
            type="button"
            className="m-btn m-btn--primary"
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      }
    >
      <div className="m-form">
        {isSeries ? (
          <div className="m-field">
            <span className="m-field__label">Appliquer à</span>
            <div className="m-pills">
              {SCOPES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className="m-pill"
                  data-active={scope === s.value}
                  onClick={() => setScope(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {canChangeAffaire ? (
          <label className="m-field">
            <span className="m-field__label">Affaire</span>
            <select
              className="m-input"
              value={affaireId}
              onChange={(e) => {
                setAffaireId(e.target.value);
                setPhaseId('');
                setTask(NO_TASK);
              }}
            >
              <option value="">— Choisir une affaire —</option>
              {affaireOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} — ${a.name}` : a.name}
                </option>
              ))}
            </select>
            {affairesError ? <span className="m-field__error">{affairesError}</span> : null}
            {!affairesError && affaires.length === 0 ? (
              <span className="m-field__hint">
                Aucune affaire disponible : vous devez faire partie de l’équipe d’une affaire.
              </span>
            ) : null}
          </label>
        ) : (
          <div className="m-field">
            <span className="m-field__label">Affaire</span>
            <span className="m-field__value">{allocation.affaire?.name || '—'}</span>
          </div>
        )}

        {phases.length > 0 ? (
          <label className="m-field">
            <span className="m-field__label">Phase</span>
            <select className="m-input" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
              <option value="">— Aucune —</option>
              {phases.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {isSeries ? null : (
          <div className="m-field-row">
            <label className="m-field">
              <span className="m-field__label">Du</span>
              <input
                className="m-input"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (endDate && e.target.value > endDate) setEndDate(e.target.value);
                }}
              />
            </label>
            <label className="m-field">
              <span className="m-field__label">Au</span>
              <input
                className="m-input"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>
        )}

        <MobileTaskField
          affaireId={affaireId}
          value={task}
          onChange={setTask}
          disabled={submitting}
        />

        <label className="m-field">
          <span className="m-field__label">Heures par jour</span>
          <select className="m-input" value={hours} onChange={(e) => setHours(e.target.value)}>
            {HOURS_OPTIONS.map((h) => (
              <option key={h} value={String(h)}>
                {h} h
              </option>
            ))}
          </select>
        </label>

        <label className="m-field">
          <span className="m-field__label">Notes</span>
          <textarea
            className="m-input"
            rows={3}
            maxLength={10000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        {error ? (
          <div className="m-field__error" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
